import React, { useState, useEffect } from "react";

// ─── Geometry (desktop) ─────────────────────────────────────────────────────
const W = 1200, H = 740;
const CX = W / 2, CY = H / 2;
const HEX_R = 158, CORNER_LINE_LEN = 200;
const CARD_W = 184, CARD_H = 180;

const deg2rad = d => (d * Math.PI) / 180;

const VERTEX_ANGLES = [0, 60, 120, 180, 240, 300];
const EDGE_ANGLES   = [30, 90, 150, 210, 270, 330];
const CARD_DISTS    = [290, 262, 290, 290, 262, 290];

const hexVerts = VERTEX_ANGLES.map(a => ({
  x: CX + HEX_R * Math.cos(deg2rad(a)),
  y: CY - HEX_R * Math.sin(deg2rad(a)),
}));

const cardCenters = EDGE_ANGLES.map((a, i) => ({
  x: CX + CARD_DISTS[i] * Math.cos(deg2rad(a)),
  y: CY - CARD_DISTS[i] * Math.sin(deg2rad(a)),
}));

const cornerLineEnds = VERTEX_ANGLES.map(a => ({
  x: CX + (HEX_R + CORNER_LINE_LEN) * Math.cos(deg2rad(a)),
  y: CY - (HEX_R + CORNER_LINE_LEN) * Math.sin(deg2rad(a)),
}));

const hexPointsStr = hexVerts.map(v => `${v.x},${v.y}`).join(" ");

// ─── Data ──────────────────────────────────────────────────────────────────
const values = [
  { key: "security",       title: "Security",                  icon: "lock",      desc: "Our main mission is to keep customers' data confidential and secure. We strive to keep our people, customers and the environment safe." },
  { key: "customers",      title: "Commitment to Customers",   icon: "handshake", desc: "We provide a service that is valued by our customers, exceeding their expectations and building ever-lasting relationships." },
  { key: "passion",        title: "Passion",                   icon: "heart",     desc: "Grow our business in a way that makes us proud by embracing and driving change. Passion and personality matter." },
  { key: "environment",    title: "Commitment to Environment", icon: "tree",      desc: "We use recycling methods in innovative ways to help make the world a better place." },
  { key: "family",         title: "Family",                    icon: "family",    desc: "Creating a culture of warmth and belonging where everyone is welcome as part of the family." },
  { key: "accountability", title: "Accountability",            icon: "star",      desc: "We take responsibility for all that we do. We keep every commitment and promise made to others." },
];

// ─── Icons ─────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 26 }) => {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "#1a2980", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "handshake": return <svg {...p}><path d="M3 10h2l2-2h6l2 2h2"/><path d="M3 10v6a1 1 0 0 0 1 1h1"/><path d="M19 10v6a1 1 0 0 1-1 1h-1"/><path d="M9 8v10"/><path d="M15 8v10"/></svg>;
    case "lock":      return <svg {...p}><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V8a5 5 0 0 1 10 0v3"/><circle cx="12" cy="16" r="1" fill="#1a2980"/></svg>;
    case "heart":     return <svg {...p}><path d="M20.8 4.6a4.6 4.6 0 0 0-6.5 0L12 6.9 9.7 4.6A4.6 4.6 0 1 0 3.2 11l8.8 8.9L20.8 11A4.6 4.6 0 0 0 20.8 4.6z"/></svg>;
    case "family":    return <svg {...p}><circle cx="9" cy="7" r="2"/><circle cx="15" cy="7" r="2"/><path d="M4 20v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/></svg>;
    case "star":      return <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    case "tree":      return <svg {...p}><path d="M12 2L8 8h3L7 14h4v6h2v-6h4l-4-6h3L12 2z"/></svg>;
    default: return null;
  }
};

// ─── Small decorative hex (mobile/tablet header) ────────────────────────────
const SmallHex = ({ size = 120 }) => {
  const r = size / 2 - 8;
  const cx = size / 2, cy = size / 2;
  const verts = VERTEX_ANGLES.map(a => ({ x: cx + r * Math.cos(deg2rad(a)), y: cy - r * Math.sin(deg2rad(a)) }));
  const pts = verts.map(v => `${v.x},${v.y}`).join(" ");
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <linearGradient id="hexGradSm" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#26d0ce"/>
          <stop offset="100%" stopColor="#1a2980"/>
        </linearGradient>
      </defs>
      <polygon points={pts} fill="url(#hexGradSm)"/>
      <polygon
        points={verts.map(v => `${cx + (v.x - cx) * 0.8},${cy + (v.y - cy) * 0.8}`).join(" ")}
        fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1"
      />
    </svg>
  );
};

// ─── Mobile card ────────────────────────────────────────────────────────────
const MobileCard = ({ v }) => (
  <div style={{
    background: "#fff",
    borderRadius: 16,
    padding: "20px 18px",
    boxShadow: "0 4px 24px rgba(26,41,128,0.10)",
    border: "1px solid rgba(38,208,206,0.18)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 10,
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: "50%",
      border: "2px solid #1a2980",
      background: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 10px rgba(26,41,128,0.12)",
      flexShrink: 0,
    }}>
      <Icon name={v.icon} size={22}/>
    </div>
    <div style={{ fontSize: 11.5, fontWeight: 800, fontFamily: "'Helvetica Neue', Arial, sans-serif", color: "#1a2980", letterSpacing: 0.5, textTransform: "uppercase", lineHeight: 1.3 }}>
      {v.title}
    </div>
    <p style={{ fontSize: 12, color: "#2c3e50", lineHeight: 1.65, margin: 0, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
      {v.desc}
    </p>
  </div>
);

// ─── Tablet layout: scaled SVG hex ─────────────────────────────────────────
const TabletLayout = ({ scale }) => (
  <div style={{
    position: "relative",
    width: W * scale,
    height: H * scale,
    margin: "0 auto",
  }}>
    <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: W, height: H, position: "absolute", top: 0, left: 0 }}>
      <HexSVG/>
      <CenterLabel/>
      {values.map((v, i) => {
        const cc = cardCenters[i];
        return (
          <div key={v.key} style={{
            position: "absolute",
            left: cc.x - CARD_W / 2,
            top:  cc.y - CARD_H / 2,
            width: CARD_W, height: CARD_H,
            zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", textAlign: "center",
            justifyContent: "center", gap: 7,
          }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px solid #1a2980", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 12px rgba(26,41,128,0.12)" }}>
              <Icon name={v.icon}/>
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'Helvetica Neue', Arial, sans-serif", color: "#1a2980", letterSpacing: 0.4, lineHeight: 1.3, textTransform: "uppercase" }}>
              {v.title}
            </div>
            <p style={{ fontSize: 11.5, color: "#2c3e50", lineHeight: 1.6, margin: 0, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
              {v.desc}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

const HexSVG = () => (
  <svg width={W} height={H} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
    <defs>
      <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#26d0ce"/>
        <stop offset="100%" stopColor="#1a2980"/>
      </linearGradient>
      <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="8" stdDeviation="18" floodColor="#1a298028"/>
      </filter>
    </defs>
    {hexVerts.map((v, i) => (
      <line key={`cl-${i}`} x1={v.x} y1={v.y} x2={cornerLineEnds[i].x} y2={cornerLineEnds[i].y}
        stroke="#26d0ce" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65"/>
    ))}
    {cornerLineEnds.map((p, i) => (
      <circle key={`ct-${i}`} cx={p.x} cy={p.y} r="3" fill="#26d0ce" opacity="0.5"/>
    ))}
    <polygon points={hexPointsStr} fill="url(#hexGrad)" filter="url(#softShadow)"/>
    <polygon
      points={hexVerts.map(v => `${CX + (v.x - CX) * 0.80},${CY + (v.y - CY) * 0.80}`).join(" ")}
      fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5"
    />
    {hexVerts.map((v, i) => (
      <circle key={`hv-${i}`} cx={v.x} cy={v.y} r="5"
        fill="rgba(38,208,206,0.35)" stroke="#26d0ce" strokeWidth="1.5"/>
    ))}
  </svg>
);

const CenterLabel = () => (
  <div style={{
    position: "absolute", left: CX, top: CY,
    transform: "translate(-50%, -50%)",
    textAlign: "center", zIndex: 5, color: "#fff",
    pointerEvents: "none", userSelect: "none",
    display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1,
  }}>
    <span style={{ fontSize: 14, fontFamily: "'Helvetica Neue', Arial, sans-serif", fontWeight: 400, letterSpacing: 5, opacity: 0.85, marginBottom: 4 }}>OUR</span>
    <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: 3, fontFamily: "'Georgia', serif" }}>CORE</span>
    <span style={{ fontSize: 44, fontWeight: 800, letterSpacing: 3, fontFamily: "'Georgia', serif" }}>VALUES</span>
  </div>
);

// ─── Desktop layout (original) ──────────────────────────────────────────────
const DesktopLayout = () => (
  <div style={{ position: "relative", width: W, maxWidth: "100%", height: H, margin: "0 auto" }}>
    <HexSVG/>
    <CenterLabel/>
    {values.map((v, i) => {
      const cc = cardCenters[i];
      return (
        <div key={v.key} style={{
          position: "absolute",
          left: cc.x - CARD_W / 2, top: cc.y - CARD_H / 2,
          width: CARD_W, height: CARD_H, zIndex: 10,
          display: "flex", flexDirection: "column",
          alignItems: "center", textAlign: "center",
          justifyContent: "center", gap: 7,
        }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px solid #1a2980", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 12px rgba(26,41,128,0.12)" }}>
            <Icon name={v.icon}/>
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'Helvetica Neue', Arial, sans-serif", color: "#1a2980", letterSpacing: 0.4, lineHeight: 1.3, textTransform: "uppercase" }}>
            {v.title}
          </div>
          <p style={{ fontSize: 11.5, color: "#2c3e50", lineHeight: 1.6, margin: 0, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
            {v.desc}
          </p>
        </div>
      );
    })}
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const ValuesSection = () => {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Breakpoints
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1100;
  const tabletScale = isTablet ? Math.min((width - 40) / W, 0.80) : 1;

  return (
    <section
      style={{
        background: "linear-gradient(160deg, #eef6f5 0%, #f4fafa 50%, #e8f4f2 100%)",
        padding: isMobile ? "28px 16px 36px" : isTablet ? "32px 20px 40px" : "32px 20px 40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontFamily: "'Georgia', 'Times New Roman', serif",
        overflow: "hidden",
        boxSizing: "border-box",
        width: "100%",
      }}
      aria-label="Our Core Values"
    >
      {/* Eyebrow */}
      <p style={{
        margin: "0 0 8px",
        fontSize: 12,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontWeight: 600,
        letterSpacing: 3,
        textTransform: "uppercase",
        color: "#26d0ce",
        textAlign: "center",
      }}>
        What drives us every day
      </p>

      {/* ── MOBILE ── */}
      {isMobile && (
        <>
          {/* Mini hex + title header - with smaller "CORE VALUES" font */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 32, gap: 0 }}>
            <div style={{ position: "relative", width: 110, height: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SmallHex size={110}/>
              <div style={{
                position: "absolute", inset: 0, display: "flex",
                flexDirection: "column", alignItems: "center", justifyContent: "center",
                color: "#fff", lineHeight: 1, gap: 0,
              }}>
                <span style={{ fontSize: 7, fontFamily: "'Helvetica Neue', Arial, sans-serif", letterSpacing: 3, opacity: 0.85 }}>OUR</span>
                <span style={{ fontSize: 14, fontWeight: 800, fontFamily: "'Georgia', serif", letterSpacing: 1 }}>CORE</span>
                <span style={{ fontSize: 14, fontWeight: 800, fontFamily: "'Georgia', serif", letterSpacing: 1 }}>VALUES</span>
              </div>
            </div>
          </div>

          {/* 2-column card grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            width: "100%",
            maxWidth: 480,
          }}>
            {values.map(v => <MobileCard key={v.key} v={v}/>)}
          </div>
        </>
      )}

      {/* ── TABLET ── */}
      {isTablet && (
        <div style={{ width: "100%", overflow: "hidden" }}>
          <TabletLayout scale={tabletScale}/>
        </div>
      )}

      {/* ── DESKTOP ── */}
      {!isMobile && !isTablet && <DesktopLayout/>}
    </section>
  );
};

export default ValuesSection;